import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { useDegreeContext } from 'bundles/degree-home/components/v2/DegreeHomeContext';
import type { CoursePlanDialogType } from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/context/CoursePlanningDialogContext';
import type {
  LearnerPlan_CreatePlanMutation as CreatePlanMutationData,
  LearnerPlan_CreatePlanMutationVariables as CreatePlanVariables,
} from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/createPlanMutation';
import type { UseCurriculum_PersonalizedSegmentCourseFragment as SegmentCourse } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import LearnerPlanCreatePlanMutation from 'bundles/degree-home/components/v3-dpm/lib/__queries__/createPlanMutation.graphql';

import useDegreePlan from './useDegreePlan';

export default function useCreatePlan() {
  const { updateQuery } = useDegreePlan();
  const [createPlanMutation, mutationState] = useMutation<CreatePlanMutationData, CreatePlanVariables>(
    LearnerPlanCreatePlanMutation,
    { context: { clientName: 'gatewayGql' } }
  );
  const { degreeId } = useDegreeContext();

  const createPlan = async ({
    openCourseDialog,
  }: {
    openCourseDialog: (course: SegmentCourse | null, dialogType: CoursePlanDialogType) => void;
  }) => {
    try {
      const res = await createPlanMutation({
        variables: {
          createPlanInput: {
            degreeId,
          },
        },
        context: {
          clientName: 'gatewayGql',
        },
      });

      const mutationResult = res.data?.LearnerPlan_CreatePlan;

      if (mutationResult?.__typename !== 'LearnerPlan_LearnerPlanResult' || mutationState.error || res.errors?.length) {
        openCourseDialog(null, 'error');
        return false;
      }

      updateQuery(() => {
        return {
          __typename: 'Query',
          LearnerPlan: {
            __typename: 'LearnerPlanQueries',
            queryPlan: mutationResult,
          },
        };
      });
      return mutationResult;
    } catch (e) {
      Sentry.captureException(e);
      openCourseDialog(null, 'error');
      return false;
    }
  };

  return {
    createPlan,
    createPlanLoading: mutationState.loading,
  };
}
